import { cn } from '@/lib/utils';
import {
  Briefcase,
  FileText,
  LayoutDashboard,
  Mail,
  PieChart,
  TrendingUp,
  X,
} from 'lucide-react';
import React from 'react';
import { Link, useLocation } from 'react-router';
import type { PortalModule } from '@/types/investor';
import { usePortalAccess } from '../hooks/usePortalAccess';
import dffLogo from '@/assets/dff-logo-blue.svg';

// Sidebar width constant
const SIDEBAR_WIDTH = 'w-64';

interface SidebarProps {
  isMobileMenuOpen: boolean;
  onClose: () => void;
}

interface NavigationItem {
  label: string;
  href: string;
  icon: React.ElementType;
  description?: string;
  primary?: boolean;
  module?: PortalModule; // Module required to access this item
}

const navigation: NavigationItem[] = [
  {
    label: 'Dashboard',
    href: '/portal',
    icon: LayoutDashboard,
    primary: true,
    module: 'dashboard',
  },
  {
    label: 'Holdings',
    href: '/portal/holdings',
    icon: Briefcase,
    primary: true,
    module: 'holdings',
  },
  {
    label: 'Transactions',
    href: '/portal/transactions',
    icon: TrendingUp,
    primary: true,
    module: 'transactions',
  },
  {
    label: 'Fund carry',
    href: '/portal/carry',
    icon: PieChart,
    primary: true,
    module: 'fund_carry',
  },
  {
    label: 'Documents',
    href: '/portal/documents',
    icon: FileText,
    module: 'documents',
  },
  {
    label: 'Emails',
    href: '/portal/emails',
    icon: Mail,
    module: 'documents',
  },
];

export function PortalSidebar({ isMobileMenuOpen, onClose }: SidebarProps) {
  const location = useLocation();
  const { canAccess } = usePortalAccess();

  // Filter navigation items based on accessible modules
  const filteredNavigation = navigation.filter((item) => {
    // If no module is specified, show the item (profile pages, etc.)
    if (!item.module) return true;
    return canAccess(item.module);
  });

  const isActive = (href: string) => {
    if (href === '/portal') {
      return location.pathname === '/portal';
    }
    return location.pathname.startsWith(href);
  };

  const handleNavClick = () => {
    onClose();
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-card border-r border-border transition-transform duration-200 ease-in-out lg:translate-x-0 lg:static lg:z-auto',
          SIDEBAR_WIDTH,
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo section */}
        <div className="flex h-16 items-center justify-between px-6 border-b border-border">
          <Link to="/portal" className="flex items-center gap-1">
            <img
              src={dffLogo}
              alt="DFF"
              className="h-7 w-auto dark:brightness-0 dark:invert"
            />
            <span className="text-lg font-light text-muted-foreground">
              Flow
            </span>
          </Link>
          {/* Mobile close button */}
          <button
            onClick={onClose}
            className="lg:hidden p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {filteredNavigation.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);

            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={handleNavClick}
                className={cn(
                  'group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150',
                  active
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                )}
              >
                <Icon
                  className={cn(
                    'h-5 w-5 flex-shrink-0 transition-colors',
                    active
                      ? 'text-primary'
                      : 'text-muted-foreground group-hover:text-foreground'
                  )}
                />
                <span className="truncate">{item.label}</span>
                {active && (
                  <div className="ml-auto h-1.5 w-1.5 rounded-full bg-primary" />
                )}
              </Link>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
